import { Artwork } from "kreate-common/modules/business-types";
import { httpPost$LoveArtwork } from "kreate-server/commands/LoveArtwork/fetcher";
import { useRouter } from "next/router";
import { MdWarning } from "react-icons/md";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import { getLinkToPageLogin } from "@/modules/navigation/utils/getLinkTo";

export function useArtworkLover(artwork: Artwork) {
  const loginStatus = useLoginStatus();
  const router = useRouter();

  const modalTriggers = {
    ModalFailure: useModalTrigger$ModalFailure(),
    ModalConfirm: useModalTrigger$ModalConfirm(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler((error) =>
    modalTriggers.ModalFailure.openModalWithError(error)
  );

  const isLoved =
    !!loginStatus?.loggedIn && !!artwork?.lovedBy?.includes(loginStatus.userId);

  const numLovers = artwork.lovedBy.length;

  const toggleLove = withErrorHandler(async () => {
    if (!loginStatus?.loggedIn) {
      const confirmed = await modalTriggers.ModalConfirm.openModal(
        "You have to login to perform this action.",
        {
          icon: <MdWarning />,
          labelButtonAccept: "Login",
          labelButtonReject: "Cancel",
        }
      );
      if (confirmed) {
        await router.push(
          getLinkToPageLogin({
            redirectUrl: location.pathname + location.search + location.hash,
          })
        );
      }
      return;
    }

    await httpPost$LoveArtwork(`/api/v1/artwork/${artwork.artworkId}/love`, {
      love: !isLoved,
    });
    await shamelesslyRevalidateEverything();
  });

  return { busy, isLoved, numLovers, toggleLove };
}
